import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, MapPin, Edit2, Save, X, Loader2, Camera, Trash2, AlertTriangle, Shield, LogOut, Smartphone, Sun, Moon, Copy, Globe, Coins, CheckCircle2, Clock, Languages, EyeOff, FileText, Upload, Building2, Link2 } from 'lucide-react';
import { toast } from '../utils/toastWithSound';
import axios from '../lib/axios';
import { useAuth, API, useTheme, useIsland, useCurrency, useLanguage } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardContainer, PageHeader } from '../components/DashboardLayout';
import ReferralProgressCard from '../components/ReferralProgressCard';
import SafariDropdown from '../components/ui/safari-dropdown';

// Translations
const translations = {
  en: {
    location: 'Location',
    locationDesc: 'Browse items from',
    currency: 'Currency',
    currencyDesc: 'Display prices in',
    language: 'Language',
    languageDesc: 'App language',
    theme: 'Theme',
    themeLight: 'Light mode',
    themeDark: 'Dark mode',
    switchTo: 'Switch to',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    locationField: 'Location',
    paymentMethods: 'Payment Methods',
    whatsapp: 'WhatsApp',
    usdt: 'USDT (ERC20)',
    add: 'Add',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    verify: 'Verify',
    open: 'Open',
    copied: 'Copied!',
    memberSince: 'Member since',
    security: 'Security',
    activeSessions: 'Active Sessions',
    activeSessionsDesc: 'Sign out from all devices',
    logoutAll: 'Logout All',
    logoutAllTitle: 'Logout from All Devices?',
    logoutAllDesc: 'This will sign you out from all devices and invalidate all sessions.',
    passwordNote: 'To change password, use "Forgot Password" on login page.',
    dangerZone: 'Danger Zone',
    deleteAccount: 'Delete Account',
    deleteAccountDesc: 'Permanently delete all data',
    delete: 'Delete',
    deleteConfirmTitle: 'Delete Account',
    deleteConfirmDesc: 'This will permanently delete your account and all data. This cannot be undone.',
    deleteConfirmType: 'Type DELETE to confirm:',
    changeBanner: 'Change',
    addBanner: 'Add',
    // Privacy settings
    privacy: 'Privacy',
    privacyDesc: 'Control visibility of your contact info',
    hidePhone: 'Hide phone number',
    hidePhoneDesc: 'Your phone won\'t be shown on your listings',
    hideEmail: 'Hide email address',
    hideEmailDesc: 'Your email won\'t be shown on your listings',
    enableWhatsapp: 'Enable WhatsApp contact',
    enableWhatsappDesc: 'Show WhatsApp button on your listings',
    // Business verification
    businessVerification: 'Business Verification',
    businessVerificationDesc: 'Upload your documents to verify your business',
    documentsSubmitted: 'Documents Submitted',
    documentsSubmittedDesc: 'Your documents are being reviewed',
    documentsApproved: 'Verified Business',
    documentsApprovedDesc: 'Your business has been verified',
    uploadKvk: 'Upload KVK Document',
    uploadId: 'Upload ID Document',
    submitDocuments: 'Submit Documents',
    idCard: 'ID Card',
    passport: 'Passport',
    // Upgrade to business
    upgradeToBusiness: 'Upgrade to Business',
    upgradeToBusinessDesc: 'Convert your account to a business account',
    businessNameLabel: 'Business Name',
    kvkNumberLabel: 'KVK Number',
    upgradeNow: 'Upgrade Now',
    // Business website
    website: 'Website',
    websitePlaceholder: 'https://www.example.com',
  },
  nl: {
    location: 'Locatie',
    locationDesc: 'Bekijk items van',
    currency: 'Valuta',
    currencyDesc: 'Toon prijzen in',
    language: 'Taal',
    languageDesc: 'App taal',
    theme: 'Thema',
    themeLight: 'Lichte modus',
    themeDark: 'Donkere modus',
    switchTo: 'Wissel naar',
    name: 'Naam',
    email: 'E-mail',
    phone: 'Telefoon',
    locationField: 'Locatie',
    paymentMethods: 'Betaalmethodes',
    whatsapp: 'WhatsApp',
    usdt: 'USDT (ERC20)',
    add: 'Toevoegen',
    edit: 'Bewerken',
    save: 'Opslaan',
    cancel: 'Annuleren',
    verify: 'Verifiëren',
    open: 'Open',
    copied: 'Gekopieerd!',
    memberSince: 'Lid sinds',
    security: 'Beveiliging',
    activeSessions: 'Actieve Sessies',
    activeSessionsDesc: 'Uitloggen op alle apparaten',
    logoutAll: 'Alles Uitloggen',
    logoutAllTitle: 'Uitloggen op alle apparaten?',
    logoutAllDesc: 'Dit zal je uitloggen op alle apparaten en alle sessies beëindigen.',
    passwordNote: 'Om je wachtwoord te wijzigen, gebruik "Wachtwoord vergeten" op de login pagina.',
    dangerZone: 'Gevarenzone',
    deleteAccount: 'Account Verwijderen',
    deleteAccountDesc: 'Verwijder alle gegevens permanent',
    delete: 'Verwijderen',
    deleteConfirmTitle: 'Account Verwijderen',
    deleteConfirmDesc: 'Dit verwijdert je account en alle gegevens permanent. Dit kan niet ongedaan worden.',
    deleteConfirmType: 'Typ DELETE om te bevestigen:',
    changeBanner: 'Wijzig',
    addBanner: 'Toevoegen',
    // Privacy settings
    privacy: 'Privacy',
    privacyDesc: 'Beheer zichtbaarheid van je contactgegevens',
    hidePhone: 'Telefoonnummer verbergen',
    hidePhoneDesc: 'Je telefoon wordt niet getoond op je advertenties',
    hideEmail: 'E-mailadres verbergen',
    hideEmailDesc: 'Je e-mail wordt niet getoond op je advertenties',
    enableWhatsapp: 'WhatsApp contact inschakelen',
    enableWhatsappDesc: 'Toon WhatsApp knop op je advertenties',
    // Business verification
    businessVerification: 'Bedrijfsverificatie',
    businessVerificationDesc: 'Upload je documenten om je bedrijf te verifiëren',
    documentsSubmitted: 'Documenten Ingediend',
    documentsSubmittedDesc: 'Je documenten worden beoordeeld',
    documentsApproved: 'Geverifieerd Bedrijf',
    documentsApprovedDesc: 'Je bedrijf is geverifieerd',
    uploadKvk: 'Upload KVK-uittreksel',
    uploadId: 'Upload ID-bewijs',
    submitDocuments: 'Documenten Indienen',
    idCard: 'ID-kaart',
    passport: 'Paspoort',
    // Upgrade to business
    upgradeToBusiness: 'Upgrade naar Zakelijk',
    upgradeToBusinessDesc: 'Zet je account om naar een zakelijk account',
    businessNameLabel: 'Bedrijfsnaam',
    kvkNumberLabel: 'KVK-nummer',
    upgradeNow: 'Nu Upgraden',
    // Business website
    website: 'Website',
    websitePlaceholder: 'https://www.voorbeeld.nl',
  },
  es: {
    location: 'Ubicación',
    locationDesc: 'Ver artículos de',
    currency: 'Moneda',
    currencyDesc: 'Mostrar precios en',
    language: 'Idioma',
    languageDesc: 'Idioma de la app',
    theme: 'Tema',
    themeLight: 'Modo claro',
    themeDark: 'Modo oscuro',
    switchTo: 'Cambiar a',
    name: 'Nombre',
    email: 'Correo',
    phone: 'Teléfono',
    locationField: 'Ubicación',
    paymentMethods: 'Métodos de Pago',
    whatsapp: 'WhatsApp',
    usdt: 'USDT (ERC20)',
    add: 'Agregar',
    edit: 'Editar',
    save: 'Guardar',
    cancel: 'Cancelar',
    verify: 'Verificar',
    open: 'Abrir',
    copied: '¡Copiado!',
    memberSince: 'Miembro desde',
    security: 'Seguridad',
    activeSessions: 'Sesiones Activas',
    activeSessionsDesc: 'Cerrar sesión en todos los dispositivos',
    logoutAll: 'Cerrar Todo',
    logoutAllTitle: '¿Cerrar sesión en todos los dispositivos?',
    logoutAllDesc: 'Esto cerrará tu sesión en todos los dispositivos e invalidará todas las sesiones.',
    passwordNote: 'Para cambiar tu contraseña, usa "Olvidé mi contraseña" en la página de inicio.',
    dangerZone: 'Zona de Peligro',
    deleteAccount: 'Eliminar Cuenta',
    deleteAccountDesc: 'Eliminar todos los datos permanentemente',
    delete: 'Eliminar',
    deleteConfirmTitle: 'Eliminar Cuenta',
    deleteConfirmDesc: 'Esto eliminará tu cuenta y todos tus datos permanentemente. No se puede deshacer.',
    deleteConfirmType: 'Escribe DELETE para confirmar:',
    changeBanner: 'Cambiar',
    addBanner: 'Agregar',
    // Privacy settings
    privacy: 'Privacidad',
    privacyDesc: 'Controla la visibilidad de tu información de contacto',
    hidePhone: 'Ocultar número de teléfono',
    hidePhoneDesc: 'Tu teléfono no se mostrará en tus anuncios',
    hideEmail: 'Ocultar correo electrónico',
    hideEmailDesc: 'Tu correo no se mostrará en tus anuncios',
    enableWhatsapp: 'Habilitar contacto WhatsApp',
    enableWhatsappDesc: 'Mostrar botón de WhatsApp en tus anuncios',
    // Business verification
    businessVerification: 'Verificación de Negocio',
    businessVerificationDesc: 'Sube tus documentos para verificar tu negocio',
    documentsSubmitted: 'Documentos Enviados',
    documentsSubmittedDesc: 'Tus documentos están siendo revisados',
    documentsApproved: 'Negocio Verificado',
    documentsApprovedDesc: 'Tu negocio ha sido verificado',
    uploadKvk: 'Subir documento KVK',
    uploadId: 'Subir documento de ID',
    submitDocuments: 'Enviar Documentos',
    idCard: 'Tarjeta de ID',
    passport: 'Pasaporte',
    // Upgrade to business
    upgradeToBusiness: 'Actualizar a Negocio',
    upgradeToBusinessDesc: 'Convierte tu cuenta en una cuenta de negocio',
    businessNameLabel: 'Nombre del Negocio',
    kvkNumberLabel: 'Número KVK',
    upgradeNow: 'Actualizar Ahora',
    // Business website
    website: 'Sitio Web',
    websitePlaceholder: 'https://www.ejemplo.com',
  }
};

// WhatsApp Icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

// USDT Icon  
const USDTIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 32 32" fill="currentColor">
    <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0zm7.994 18.394c-.093.387-.307.68-.64.88-.333.2-.74.3-1.22.3H17.6v4.36h-3.2v-4.36H9.866c-.48 0-.887-.1-1.22-.3-.333-.2-.547-.493-.64-.88V17.1h13.988v1.294zm0-3.2H8.006v-1.28c.093-.4.307-.7.64-.9.333-.2.74-.3 1.22-.3H14.4V8.34h3.2v4.374h4.534c.48 0 .887.1 1.22.3.333.2.547.5.64.9v1.28z"/>
  </svg>
);

const ProfilePage = () => {
  const { user, token, updateProfile, fetchUser, logout, logoutAllDevices } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { currentIsland, setCurrentIsland } = useIsland();
  const { currency, setCurrency } = useCurrency();
  const { language, setLanguage } = useLanguage();
  
  const t = translations[language] || translations.en;
  
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    whatsapp: user?.whatsapp || '',
    location: user?.location || '',
    usdt_erc20_address: user?.usdt_erc20_address || '',
    website: user?.website || '',
  });
  
  // Verification state
  const [verification, setVerification] = useState(null);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [showLogoutAllModal, setShowLogoutAllModal] = useState(false);
  
  // Privacy settings state
  const [hidePhone, setHidePhone] = useState(user?.hide_phone || false);
  const [hideEmail, setHideEmail] = useState(user?.hide_email || false);
  const [enableWhatsapp, setEnableWhatsapp] = useState(user?.enable_whatsapp || false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  // Business document upload state
  const [kvkDocument, setKvkDocument] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  const [idType, setIdType] = useState('id_card');
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const kvkInputRef = useRef(null);
  const idInputRef = useRef(null);

  // Upgrade to business state
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);
  const [upgradeBusinessName, setUpgradeBusinessName] = useState('');
  const [upgradeKvkNumber, setUpgradeKvkNumber] = useState('');
  const [upgradingAccount, setUpgradingAccount] = useState(false);

  // Allow avatar for all users (removed account_type restriction)
  const canUploadAvatar = true;
  const canUploadBanner = user?.account_type === 'business' || user?.is_verified_business;
  const isBusinessAccount = user?.account_type === 'business';
  const isPendingBusinessUpgrade = user?.pending_business_upgrade === true;
  const documentsSubmitted = user?.business_documents_submitted;
  const documentsStatus = user?.business_documents_status;
  const isVerifiedBusiness = user?.is_verified_business || documentsStatus === 'approved';
  
  // Show business verification card if:
  // 1. User has pending upgrade request (filled in business details)
  // 2. OR user is already a business account (for re-verification or status check)
  const showBusinessVerificationCard = isPendingBusinessUpgrade || isBusinessAccount;
  
  // Show upgrade card only for personal accounts without pending upgrade
  const showUpgradeCard = !isBusinessAccount && !isPendingBusinessUpgrade;

  // Options for dropdowns
  const islandOptions = [
    { value: 'curacao', label: '🇨🇼 Curaçao' },
    { value: 'aruba', label: '🇦🇼 Aruba' },
    { value: 'bonaire', label: '🇧🇶 Bonaire' },
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'XCG', label: 'XCG (ƒ)' },
  ];

  const languageOptions = [
    { value: 'nl', label: '🇳🇱 Nederlands' },
    { value: 'en', label: '🇬🇧 English' },
    { value: 'es', label: '🇪🇸 Español' },
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        location: user.location || '',
        usdt_erc20_address: user.usdt_erc20_address || '',
        website: user.website || '',
      });
      setHidePhone(user.hide_phone || false);
      setHideEmail(user.hide_email || false);
      setEnableWhatsapp(user.enable_whatsapp || false);
      fetchVerification();
    }
  }, [user]);

  const fetchVerification = async () => {
    try {
      const response = await axios.get(`${API}/users/me/verification`);
      setVerification(response.data);
    } catch (error) {
      console.error('Failed to fetch verification:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.success(language === 'nl' ? 'Profiel bijgewerkt' : language === 'es' ? 'Perfil actualizado' : 'Profile updated');
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      whatsapp: user?.whatsapp || '',
      location: user?.location || '',
      usdt_erc20_address: user?.usdt_erc20_address || '',
      website: user?.website || '',
    });
    setEditing(false);
  };

  const handlePhoneVerification = async () => {
    const phoneToVerify = user?.phone;
    if (!phoneToVerify?.trim()) {
      toast.error(language === 'nl' ? 'Voer eerst een telefoonnummer in' : 'Please enter a phone number first');
      return;
    }
    setVerifyingPhone(true);
    try {
      const response = await axios.post(`${API}/users/verify/phone`, { phone_number: phoneToVerify });
      toast.success(response.data.message);
      fetchVerification();
    } catch (error) {
      toast.error('Failed to verify phone');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'nl' ? 'Afbeelding moet kleiner zijn dan 5MB' : 'Image must be less than 5MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/auth/profile-picture`, formData);
      await fetchUser();
      toast.success(language === 'nl' ? 'Avatar bijgewerkt' : 'Avatar updated');
    } catch (error) {
      toast.error(language === 'nl' ? 'Avatar uploaden mislukt' : 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Banner must be less than 10MB');
      return;
    }
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/auth/banner-picture`, formData);
      await fetchUser();
      toast.success('Banner updated');
    } catch (error) {
      toast.error('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    setLoggingOutAll(true);
    try {
      await logoutAllDevices();
      toast.success(language === 'nl' ? 'Uitgelogd op alle apparaten' : 'Logged out from all devices');
    } catch (error) {
      toast.error('Failed to logout from all devices');
      setLoggingOutAll(false);
      setShowLogoutAllModal(false);
    }
  };

  // Handle privacy toggle
  const handlePrivacyToggle = async (field, value) => {
    // Optimistic update - update UI immediately
    if (field === 'hide_phone') {
      setHidePhone(value);
    } else if (field === 'hide_email') {
      setHideEmail(value);
    } else if (field === 'enable_whatsapp') {
      setEnableWhatsapp(value);
    }
    
    setSavingPrivacy(true);
    try {
      const updateData = { [field]: value };
      await updateProfile(updateData);
      
      toast.success(language === 'nl' ? 'Privacy-instellingen bijgewerkt' : language === 'es' ? 'Configuración de privacidad actualizada' : 'Privacy settings updated');
    } catch (error) {
      toast.error(language === 'nl' ? 'Kon instellingen niet opslaan' : 'Failed to save settings');
      // Revert on error
      if (field === 'hide_phone') {
        setHidePhone(!value);
      } else if (field === 'hide_email') {
        setHideEmail(!value);
      } else if (field === 'enable_whatsapp') {
        setEnableWhatsapp(!value);
      }
    } finally {
      setSavingPrivacy(false);
    }
  };

  // Handle business document upload
  const handleBusinessDocUpload = async () => {
    if (!kvkDocument || !idDocument) {
      toast.error(language === 'nl' ? 'Upload beide documenten' : 'Please upload both documents');
      return;
    }
    
    setUploadingDocs(true);
    try {
      const formData = new FormData();
      formData.append('kvk_document', kvkDocument);
      formData.append('id_document', idDocument);
      formData.append('id_type', idType);
      
      await axios.post(`${API}/upload/business-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success(language === 'nl' ? 'Documenten ingediend voor verificatie' : 'Documents submitted for verification');
      await fetchUser(); // Refresh user data
      setKvkDocument(null);
      setIdDocument(null);
    } catch (error) {
      console.error('Document upload failed:', error);
      toast.error(language === 'nl' ? 'Upload mislukt, probeer opnieuw' : 'Upload failed, please try again');
    } finally {
      setUploadingDocs(false);
    }
  };

  // Handle upgrade to business account - submits request, doesn't make business yet
  const handleUpgradeToBusiness = async () => {
    if (!upgradeBusinessName.trim() || !upgradeKvkNumber.trim()) {
      toast.error(language === 'nl' ? 'Vul alle velden in' : 'Please fill in all fields');
      return;
    }
    
    setUpgradingAccount(true);
    try {
      // Don't set account_type to business yet - that happens after admin approval
      // Just store the business details and mark as pending upgrade
      await axios.put(`${API}/auth/profile`, {
        pending_business_upgrade: true,
        business_name: upgradeBusinessName.trim(),
        kvk_number: upgradeKvkNumber.trim()
      });
      
      toast.success(language === 'nl' ? 'Bedrijfsgegevens opgeslagen! Upload nu je documenten.' : 'Business details saved! Now upload your documents.');
      await fetchUser(); // Refresh user data
      setShowUpgradeForm(false);
      setUpgradeBusinessName('');
      setUpgradeKvkNumber('');
    } catch (error) {
      console.error('Upgrade failed:', error);
      toast.error(error.response?.data?.detail || (language === 'nl' ? 'Opslaan mislukt' : 'Save failed'));
    } finally {
      setUpgradingAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error(language === 'nl' ? 'Typ DELETE om te bevestigen' : 'Please type DELETE to confirm');
      return;
    }
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/auth/delete-account`);
      toast.success(language === 'nl' ? 'Account verwijderd' : 'Account deleted');
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
      setDeletingAccount(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navbar />

      <DashboardContainer>
        <PageHeader pageKey="profile" showBusinessBadge={true} showPublicProfileLink={true} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Sidebar - Settings & Widgets */}
          <div className="lg:col-span-4 space-y-4 order-last">
            
            {/* Location Preference */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3 mb-3">
                <Globe className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t.location}</p>
                  <p className="text-xs text-muted-foreground">{t.locationDesc}</p>
                </div>
              </div>
              <SafariDropdown
                options={islandOptions}
                value={currentIsland}
                onChange={setCurrentIsland}
                placeholder="Select island"
              />
            </div>

            {/* Currency Preference */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3 mb-3">
                <Coins className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t.currency}</p>
                  <p className="text-xs text-muted-foreground">{t.currencyDesc}</p>
                </div>
              </div>
              <SafariDropdown
                options={currencyOptions}
                value={currency}
                onChange={setCurrency}
                placeholder="Select currency"
              />
            </div>

            {/* Language Preference */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3 mb-3">
                <Languages className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t.language}</p>
                  <p className="text-xs text-muted-foreground">{t.languageDesc}</p>
                </div>
              </div>
              <SafariDropdown
                options={languageOptions}
                value={language}
                onChange={setLanguage}
                placeholder="Select language"
              />
            </div>

            {/* Theme Toggle */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
                  <div>
                    <p className="text-sm font-medium text-foreground">{t.theme}</p>
                    <p className="text-xs text-muted-foreground">{theme === 'dark' ? t.themeDark : t.themeLight}</p>
                  </div>
                </div>
                <button 
                  onClick={toggleTheme} 
                  className="px-3 py-1.5 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
                  data-testid="theme-toggle-btn"
                >
                  {theme === 'dark' ? 'Light' : 'Dark'}
                </button>
              </div>
            </div>

            {/* Share & Win Card */}
            <ReferralProgressCard />
          </div>

          {/* Main Content - Profile Details First */}
          <div className="lg:col-span-8 space-y-4">
            
            {/* Profile Card */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              
              {/* Banner - Business Only */}
              {canUploadBanner && (
                <div className="relative h-24 bg-gradient-to-r from-primary/10 to-accent/10">
                  {user.banner_url ? (
                    <img src={user.banner_url} alt="Banner" className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-xs text-muted-foreground">{language === 'nl' ? 'Aanbevolen: 1200 x 400 px' : 'Recommended: 1200 x 400 px'}</p>
                    </div>
                  )}
                  <button
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                    className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 hover:bg-black/70 text-white text-xs rounded-lg flex items-center gap-1 disabled:opacity-50"
                    data-testid="upload-banner-btn"
                  >
                    {uploadingBanner ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                    {user.banner_url ? t.changeBanner : t.addBanner}
                  </button>
                  <input type="file" ref={bannerInputRef} onChange={handleBannerUpload} accept="image/*,.heic,.heif" className="hidden" />
                </div>
              )}
              
              <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-border mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="avatar w-14 h-14 text-lg ring-2 ring-background shadow-md">
                        {user.avatar ? <img src={user.avatar} alt={user.name} /> : user.name?.charAt(0).toUpperCase()}
                      </div>
                      {canUploadAvatar && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingAvatar}
                          className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center shadow hover:bg-primary/90 disabled:opacity-50"
                          data-testid="upload-avatar-btn"
                        >
                          {uploadingAvatar ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                        </button>
                      )}
                      <input type="file" ref={fileInputRef} onChange={handleAvatarUpload} accept="image/*,.heic,.heif" className="hidden" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-foreground" data-testid="profile-name">{user.name}</h2>
                      <p className="text-sm text-muted-foreground">{t.memberSince} {new Date(user.created_at).toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'es' ? 'es-ES' : 'en-US', { month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="px-3 py-1.5 border border-primary text-primary rounded-lg text-sm hover:bg-primary/10 transition-colors flex items-center gap-1.5"
                      data-testid="edit-profile-btn"
                    >
                      <Edit2 className="w-4 h-4" />
                      {t.edit}
                    </button>
                  )}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    
                    {/* Name */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">{t.name}</p>
                        {editing ? (
                          <input type="text" name="name" value={formData.name} onChange={handleChange} className="form-input mt-0.5 text-sm" required data-testid="edit-name-input" />
                        ) : (
                          <p className="text-sm text-foreground">{user.name}</p>
                        )}
                      </div>
                    </div>

                    {/* Email - with verification status */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">{t.email}</p>
                        <p className="text-sm text-foreground">{user.email}</p>
                      </div>
                      {verification?.email_verified ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <Clock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>

                    {/* Phone - with inline verification */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 text-green-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">{t.phone}</p>
                        {editing ? (
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="form-input mt-0.5 text-sm" placeholder="+599..." data-testid="edit-phone-input" />
                        ) : (
                          <p className="text-sm text-foreground">{user.phone || <span className="text-muted-foreground">—</span>}</p>
                        )}
                      </div>
                      {/* Verification status/action */}
                      {verification?.phone_verified ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : user.phone ? (
                        <button
                          type="button"
                          onClick={handlePhoneVerification}
                          disabled={verifyingPhone}
                          className="px-2.5 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
                          data-testid="verify-phone-btn"
                        >
                          {verifyingPhone ? <Loader2 className="w-3 h-3 animate-spin" /> : t.verify}
                        </button>
                      ) : !editing ? (
                        <button type="button" onClick={() => setEditing(true)} className="text-xs text-primary hover:underline flex-shrink-0">{t.add}</button>
                      ) : null}
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">{t.locationField}</p>
                        {editing ? (
                          <input type="text" name="location" value={formData.location} onChange={handleChange} className="form-input mt-0.5 text-sm" placeholder="City" data-testid="edit-location-input" />
                        ) : (
                          <p className="text-sm text-foreground">{user.location || <span className="text-muted-foreground">—</span>}</p>
                        )}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-border pt-4">
                      <p className="text-xs text-muted-foreground font-medium mb-3">{t.paymentMethods}</p>

                      {/* USDT */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <USDTIcon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground">{t.usdt}</p>
                          {editing ? (
                            <input type="text" name="usdt_erc20_address" value={formData.usdt_erc20_address} onChange={handleChange} className="form-input mt-0.5 text-sm font-mono" placeholder="0x..." data-testid="edit-usdt-address-input" />
                          ) : (
                            <p className="text-sm text-foreground truncate font-mono">{user.usdt_erc20_address || <span className="text-muted-foreground font-sans">—</span>}</p>
                          )}
                        </div>
                        {!editing && user.usdt_erc20_address && (
                          <div className="flex gap-1.5 flex-shrink-0">
                            <button type="button" onClick={() => { navigator.clipboard.writeText(user.usdt_erc20_address); toast.success(t.copied); }} className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => setEditing(true)} className="p-1.5 bg-secondary text-foreground rounded-lg hover:bg-secondary/80">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        {!editing && !user.usdt_erc20_address && (
                          <button type="button" onClick={() => setEditing(true)} className="text-xs text-primary hover:underline">{t.add}</button>
                        )}
                      </div>

                      {/* Website - Business accounts only */}
                      {isBusinessAccount && (
                        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <Link2 className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground">{t.website}</p>
                            {editing ? (
                              <input 
                                type="url" 
                                name="website" 
                                value={formData.website} 
                                onChange={handleChange} 
                                className="form-input mt-0.5 text-sm" 
                                placeholder={t.websitePlaceholder}
                                data-testid="edit-website-input" 
                              />
                            ) : (
                              <p className="text-sm text-foreground truncate">
                                {user.website ? (
                                  <a 
                                    href={user.website.startsWith('http') ? user.website : `https://${user.website}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-primary hover:underline"
                                  >
                                    {user.website}
                                  </a>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </p>
                            )}
                          </div>
                          {!editing && user.website && (
                            <div className="flex gap-1.5 flex-shrink-0">
                              <a 
                                href={user.website.startsWith('http') ? user.website : `https://${user.website}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                              >
                                <Globe className="w-3.5 h-3.5" />
                              </a>
                              <button type="button" onClick={() => setEditing(true)} className="p-1.5 bg-secondary text-foreground rounded-lg hover:bg-secondary/80">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                          {!editing && !user.website && (
                            <button type="button" onClick={() => setEditing(true)} className="text-xs text-primary hover:underline">{t.add}</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edit Actions */}
                  {editing && (
                    <div className="mt-4 flex gap-2">
                      <button type="button" onClick={handleCancel} disabled={loading} className="flex-1 py-2 border border-border text-muted-foreground rounded-lg text-sm hover:bg-secondary disabled:opacity-50 flex items-center justify-center gap-1.5">
                        <X className="w-4 h-4" /> {t.cancel}
                      </button>
                      <button type="submit" disabled={loading} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="save-profile-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {t.save}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Privacy Card */}
            <div className="bg-card rounded-xl p-4 border border-border" data-testid="privacy-settings-card">
              <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <EyeOff className="w-4 h-4 text-primary" /> {t.privacy}
              </h3>
              <p className="text-xs text-muted-foreground mb-4">{t.privacyDesc}</p>
              
              {/* Hide Phone Toggle */}
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-foreground">{t.hidePhone}</p>
                    <p className="text-xs text-muted-foreground">{t.hidePhoneDesc}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePrivacyToggle('hide_phone', !hidePhone)}
                  disabled={savingPrivacy}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    hidePhone ? 'bg-primary' : 'bg-secondary'
                  } ${savingPrivacy ? 'opacity-50' : ''}`}
                  data-testid="hide-phone-toggle"
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      hidePhone ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              
              {/* Hide Email Toggle */}
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-foreground">{t.hideEmail}</p>
                    <p className="text-xs text-muted-foreground">{t.hideEmailDesc}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePrivacyToggle('hide_email', !hideEmail)}
                  disabled={savingPrivacy}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    hideEmail ? 'bg-primary' : 'bg-secondary'
                  } ${savingPrivacy ? 'opacity-50' : ''}`}
                  data-testid="hide-email-toggle"
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      hideEmail ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              
              {/* Enable WhatsApp Toggle */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <WhatsAppIcon className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-foreground">{t.enableWhatsapp}</p>
                    <p className="text-xs text-muted-foreground">{t.enableWhatsappDesc}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePrivacyToggle('enable_whatsapp', !enableWhatsapp)}
                  disabled={savingPrivacy || !user?.phone}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    enableWhatsapp ? 'bg-green-500' : 'bg-secondary'
                  } ${savingPrivacy || !user?.phone ? 'opacity-50' : ''}`}
                  data-testid="enable-whatsapp-toggle"
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      enableWhatsapp ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Business Verification Card - Only for business accounts */}
            {showBusinessVerificationCard && (
              <div className="mt-4 bg-card rounded-xl p-4 border border-border" data-testid="business-verification-card">
                <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary" /> {t.businessVerification}
                </h3>
                
                {isVerifiedBusiness ? (
                  // Verified status
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">{t.documentsApproved}</p>
                      <p className="text-xs text-muted-foreground">{t.documentsApprovedDesc}</p>
                    </div>
                  </div>
                ) : documentsSubmitted ? (
                  // Pending review status
                  <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">{t.documentsSubmitted}</p>
                      <p className="text-xs text-muted-foreground">{t.documentsSubmittedDesc}</p>
                    </div>
                  </div>
                ) : (
                  // Upload form
                  <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">{t.businessVerificationDesc}</p>
                    
                    {/* KVK Document Upload */}
                    <div>
                      <input
                        type="file"
                        ref={kvkInputRef}
                        accept="image/*,.pdf"
                        onChange={(e) => setKvkDocument(e.target.files[0])}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => kvkInputRef.current?.click()}
                        className={`w-full py-3 px-4 border-2 border-dashed rounded-xl flex items-center justify-center gap-2 transition-all ${
                          kvkDocument 
                            ? 'border-green-500 bg-green-500/10 text-green-600' 
                            : 'border-border hover:border-primary hover:bg-primary/5 text-muted-foreground'
                        }`}
                      >
                        {kvkDocument ? (
                          <>
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="truncate max-w-[200px]">{kvkDocument.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            <span>{t.uploadKvk}</span>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* ID Type Selection */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIdType('id_card')}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all flex items-center justify-center gap-2 ${
                          idType === 'id_card' 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-border text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        {t.idCard}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIdType('passport')}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all flex items-center justify-center gap-2 ${
                          idType === 'passport' 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-border text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        {t.passport}
                      </button>
                    </div>
                    
                    {/* ID Document Upload */}
                    <div>
                      <input
                        type="file"
                        ref={idInputRef}
                        accept="image/*,.pdf"
                        onChange={(e) => setIdDocument(e.target.files[0])}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => idInputRef.current?.click()}
                        className={`w-full py-3 px-4 border-2 border-dashed rounded-xl flex items-center justify-center gap-2 transition-all ${
                          idDocument 
                            ? 'border-green-500 bg-green-500/10 text-green-600' 
                            : 'border-border hover:border-primary hover:bg-primary/5 text-muted-foreground'
                        }`}
                      >
                        {idDocument ? (
                          <>
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="truncate max-w-[200px]">{idDocument.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            <span>{t.uploadId}</span>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* Submit Button */}
                    <button
                      type="button"
                      onClick={handleBusinessDocUpload}
                      disabled={!kvkDocument || !idDocument || uploadingDocs}
                      className="w-full py-2.5 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="submit-business-docs-btn"
                    >
                      {uploadingDocs ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>{language === 'nl' ? 'Uploaden...' : 'Uploading...'}</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>{t.submitDocuments}</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Upgrade to Business Card - Only for personal accounts without pending upgrade */}
            {showUpgradeCard && (
              <div className="mt-4 bg-card rounded-xl p-4 border border-border" data-testid="upgrade-to-business-card">
                <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary" /> {t.upgradeToBusiness}
                </h3>
                
                {!showUpgradeForm ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">{t.upgradeToBusinessDesc}</p>
                    <button
                      onClick={() => setShowUpgradeForm(true)}
                      className="w-full py-2.5 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      data-testid="show-upgrade-form-btn"
                    >
                      <Building2 className="w-4 h-4" />
                      {t.upgradeNow}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Business Name */}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">{t.businessNameLabel}</label>
                      <input
                        type="text"
                        value={upgradeBusinessName}
                        onChange={(e) => setUpgradeBusinessName(e.target.value)}
                        placeholder={language === 'nl' ? 'Bijv. Mijn Bedrijf BV' : 'e.g. My Company Ltd'}
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="upgrade-business-name-input"
                      />
                    </div>
                    
                    {/* KVK Number */}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">{t.kvkNumberLabel}</label>
                      <input
                        type="text"
                        value={upgradeKvkNumber}
                        onChange={(e) => setUpgradeKvkNumber(e.target.value)}
                        placeholder={language === 'nl' ? 'Bijv. 12345678' : 'e.g. 12345678'}
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="upgrade-kvk-input"
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowUpgradeForm(false);
                          setUpgradeBusinessName('');
                          setUpgradeKvkNumber('');
                        }}
                        className="flex-1 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:bg-secondary transition-colors"
                      >
                        {language === 'nl' ? 'Annuleren' : 'Cancel'}
                      </button>
                      <button
                        onClick={handleUpgradeToBusiness}
                        disabled={!upgradeBusinessName.trim() || !upgradeKvkNumber.trim() || upgradingAccount}
                        className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                        data-testid="confirm-upgrade-btn"
                      >
                        {upgradingAccount ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            {t.upgradeNow}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Security Card */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" /> {t.security}
              </h3>
              
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{t.activeSessions}</p>
                  <p className="text-xs text-muted-foreground">{t.activeSessionsDesc}</p>
                </div>
                <button 
                  onClick={() => setShowLogoutAllModal(true)} 
                  className="px-3 py-1.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 text-xs font-medium flex items-center gap-1.5" 
                  data-testid="logout-all-devices-btn"
                >
                  <LogOut className="w-3.5 h-3.5" /> {t.logoutAll}
                </button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-3">
                {t.passwordNote}
              </p>
            </div>

            {/* Danger Zone */}
            <div className="bg-card rounded-xl p-4 border border-red-500/20">
              <h3 className="text-base font-semibold text-red-500 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {t.dangerZone}
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{t.deleteAccount}</p>
                  <p className="text-xs text-muted-foreground">{t.deleteAccountDesc}</p>
                </div>
                <button 
                  onClick={() => setShowDeleteModal(true)} 
                  className="px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 flex items-center gap-1.5" 
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> {t.delete}
                </button>
              </div>
            </div>
          </div>
        </div>
      </DashboardContainer>

      {/* Logout All Modal */}
      {showLogoutAllModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150] flex items-center justify-center p-4" onClick={() => setShowLogoutAllModal(false)}>
          <div className="bg-card rounded-xl p-5 max-w-sm w-full border border-border" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-3">{t.logoutAllTitle}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t.logoutAllDesc}</p>
            <div className="flex gap-2">
              <button onClick={() => setShowLogoutAllModal(false)} className="flex-1 py-2 bg-secondary text-foreground rounded-lg text-sm">
                {t.cancel}
              </button>
              <button onClick={handleLogoutAllDevices} disabled={loggingOutAll} className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="confirm-logout-all-btn">
                {loggingOutAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                {t.logoutAll}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150] flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-card rounded-xl p-5 max-w-sm w-full border border-red-500/30" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-red-500 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> {t.deleteConfirmTitle}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{t.deleteConfirmDesc}</p>
            <p className="text-sm text-foreground mb-2">{t.deleteConfirmType}</p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-secondary border border-red-500/30 rounded-lg text-foreground mb-4"
              placeholder="DELETE"
              data-testid="delete-confirm-input"
            />
            <div className="flex gap-2">
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="flex-1 py-2 bg-secondary text-foreground rounded-lg text-sm">
                {t.cancel}
              </button>
              <button onClick={handleDeleteAccount} disabled={deletingAccount || deleteConfirmText !== 'DELETE'} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="confirm-delete-btn">
                {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {t.delete}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProfilePage;
